import React, { PureComponent } from 'react';

import { injectIntl, InjectedIntl } from 'react-intl';

import Button from '@atlaskit/button/standard-button';
import { Checkbox } from '@atlaskit/checkbox';
import EditorLinkIcon from '@atlaskit/icon/glyph/editor/link';
import Lozenge from '@atlaskit/lozenge';

import { Task } from 'src/components/types';
import { TaskStateChangeOptions } from 'src/redux/pull-request/types';

import messages from './tasks.i18n';
import * as styles from './tasks.style';

export type TaskProps = {
  intl: InjectedIntl;
  task: Task;
  canResolveTask?: boolean;
  isDeleting?: boolean;
  editView?: JSX.Element;
  hidePendingStatus?: boolean;
  /** Whether or not to right align the pending lozenge and comment link icon. The task list in the sidebar chooses to right align this, while inline task lists do not.*/
  rightAlignIcons?: boolean;
  onTaskStateChange: (options: TaskStateChangeOptions) => void;
  onTaskDelete?: (task: Task) => void;
  onCommentLinkClick?: (task: Task) => void;
  onTaskEditBegin?: (task: Task) => void;
};

class TaskComponent extends PureComponent<TaskProps> {
  handleTaskStateChange = () => {
    const { task } = this.props;
    this.props.onTaskStateChange({
      url: task.links.self.href,
      task,
      nextTaskState: task.state === 'RESOLVED' ? 'UNRESOLVED' : 'RESOLVED',
    });
  };

  render() {
    const {
      task,
      editView,
      canResolveTask,
      onTaskDelete,
      onCommentLinkClick,
      onTaskEditBegin,
      isDeleting,
      intl,
      hidePendingStatus,
      rightAlignIcons,
    } = this.props;
    return (
      <>
        <styles.TaskCheckboxWrapper
          rightAlignIcons={rightAlignIcons}
          pending={task.pending}
        >
          <Checkbox
            isChecked={task.state === 'RESOLVED'}
            isDisabled={
              !!editView || !canResolveTask || isDeleting || task.pending
            }
            onChange={this.handleTaskStateChange}
            label={editView || task.content.raw}
            name={`${task.id}`}
            value={task.id}
          />
          <styles.TaskIcons>
            {task.pending && !hidePendingStatus && (
              <styles.PendingLozengeContainer>
                <Lozenge appearance="new">PENDING</Lozenge>
              </styles.PendingLozengeContainer>
            )}
            {onCommentLinkClick && task.comment && (
              <Button
                onClick={() => onCommentLinkClick(task)}
                appearance="subtle"
                spacing="none"
              >
                <EditorLinkIcon
                  label={intl.formatMessage(messages.linkToTaskComment)}
                  size="medium"
                />
              </Button>
            )}
          </styles.TaskIcons>
        </styles.TaskCheckboxWrapper>
        {(onTaskEditBegin || onTaskDelete) && (
          <styles.TaskModificationLinks hasSeparator>
            {onTaskEditBegin && (
              <li>
                <Button
                  appearance="subtle-link"
                  spacing="none"
                  onClick={() => onTaskEditBegin(task)}
                  isDisabled={isDeleting}
                >
                  {intl.formatMessage(messages.editTaskLabel)}
                </Button>
              </li>
            )}
            {onTaskDelete && (
              <li>
                <Button
                  appearance="subtle-link"
                  spacing="none"
                  onClick={() => onTaskDelete(task)}
                  isDisabled={isDeleting}
                >
                  {intl.formatMessage(messages.deleteTaskLabel)}
                </Button>
              </li>
            )}
          </styles.TaskModificationLinks>
        )}
      </>
    );
  }
}

export default injectIntl(TaskComponent);
